import { getTreeEntities, getTreeRelations } from "api/entities";
import { getTreeById } from "api/trees";
import type { EntityDto, TreeDto, TreeRelationDto } from "api/types";
import { PageLoader } from "components";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Paths, { getTreeFullViewPath, getTreeViewPath } from "routes/Paths";
import { useSession } from "Session";
import { startRequest } from "utilities";

import TreeEditor from "../HomePage/components/TreeEditor";

type ApiError = {
	status?: number;
};

const TreeEditorPage = () => {
	const { t } = useTranslation("homePage");
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();
	const [searchParams, setSearchParams] = useSearchParams();
	const { getAccessToken, hasSession, signOut } = useSession();
	const [tree, setTree] = useState<TreeDto | null>(null);
	const [entities, setEntities] = useState<EntityDto[]>([]);
	const [relations, setRelations] = useState<TreeRelationDto[]>([]);
	// Entities arrive a round trip after the tree, so the canvas keeps loading after the shell renders.
	const [isCanvasLoading, setIsCanvasLoading] = useState(true);
	const initialSelectedEntityId = searchParams.get("entity");
	const onBack = useCallback((): void => navigate(Paths.root), [navigate]);
	const onOpenTreeView = useCallback((): void => {
		if (id) {
			navigate(getTreeViewPath(id));
		}
	}, [id, navigate]);
	// Leaving edit mode, and the mirror of the full view's Edit button. The selection stays behind:
	// switching modes closes the selected node's ring rather than reopening it on the other side.
	const onViewTree = useCallback((): void => {
		if (id) {
			navigate(getTreeFullViewPath(id));
		}
	}, [id, navigate]);
	// Keep the selected entity in the URL so refresh preserves the selection.
	const onSelectedEntityChange = useCallback(
		(entityId: string | null): void => {
			setSearchParams(entityId ? { entity: entityId } : {}, { replace: true });
		},
		[setSearchParams]
	);

	const loadTree = useCallback(async (): Promise<void> => {
		if (!id || !hasSession) {
			return;
		}

		try {
			const token = await getAccessToken();
			if (!token) {
				return;
			}

			// All three requests go out together: nothing here depends on the tree row, so awaiting it
			// first only added a round trip in front of the entity payload. See TreeViewPage for why the
			// old `entityCount > 0` shortcut was not worth that cost.
			const treeRequest = startRequest(getTreeById(id, token));
			const entitiesRequest = startRequest(getTreeEntities(id, token));
			const relationsRequest = startRequest(getTreeRelations(id, token));

			// Setting the tree first swaps the full-page loader for the editor shell — title, sidebar and
			// a canvas spinner — instead of holding a blank screen until the entities land.
			const loaded = await treeRequest;
			setTree(loaded);

			const [treeEntities, treeRelations] = await Promise.all([entitiesRequest, relationsRequest]);

			setEntities(treeEntities);
			setRelations(treeRelations);
			setIsCanvasLoading(false);
		} catch (error: unknown) {
			const apiError = error as ApiError;

			if (apiError.status === 401) {
				signOut();

				return;
			}

			navigate(Paths.root, { replace: true });
		}
	}, [getAccessToken, hasSession, id, navigate, signOut]);

	const onEntityCreated = useCallback((): void => {
		loadTree().catch(() => undefined);
	}, [loadTree]);

	useEffect(() => {
		loadTree().catch(() => undefined);
	}, [loadTree]);

	// Nothing of this page exists before the tree does, so the whole screen is the loading state.
	if (!tree) {
		return <PageLoader label={t("view.loadingTree")} />;
	}

	return (
		<TreeEditor
			collaborators={tree.collaborators}
			entities={entities}
			initialSelectedEntityId={initialSelectedEntityId}
			isLoading={isCanvasLoading && tree.entityCount > 0}
			onBack={onBack}
			onEntityCreated={onEntityCreated}
			onOpenTreeView={onOpenTreeView}
			onSelectedEntityChange={onSelectedEntityChange}
			onViewTree={onViewTree}
			relations={relations}
			treeId={tree.id}
			treeTitle={tree.title}
			treeVisibility={tree.visibility}
		/>
	);
};

export default TreeEditorPage;
